import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { CheckCircle, AlertCircle, Clock, XCircle, MinusCircle, ExternalLink } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  live: { key: 'live', color: 'bg-green-500', icon: CheckCircle, badge: 'bg-green-50 text-green-400' },
  synced: { key: 'synced', color: 'bg-blue-500', icon: CheckCircle, badge: 'bg-blue-900/30 text-blue-400' },
  processing: { key: 'processing', color: 'bg-yellow-500', icon: Clock, badge: 'bg-yellow-50 text-yellow-400' },
  not_synced: { key: 'not_synced', color: 'bg-red-500', icon: AlertCircle, badge: 'bg-red-50 text-red-400' },
  error: { key: 'error', color: 'bg-red-600', icon: XCircle, badge: 'bg-red-50 text-red-400' },
  unavailable: { key: 'unavailable', color: 'bg-gray-600', icon: MinusCircle, badge: 'bg-gray-800 text-gray-400' },
  opted_out: { key: 'opted_out', color: 'bg-gray-600', icon: MinusCircle, badge: 'bg-gray-800 text-gray-400' },
};

export default function ClientListings({ client, getHeaders }) {
  const { t } = useTranslation('dashboard');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetch(`${API}/api/client/dashboard/listings`, { headers: getHeaders() })
      .then(r => r.json())
      .then(d => setData(d))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [getHeaders]);

  if (loading) return <div className="text-gray-400 animate-pulse py-12 text-center">{t('listings.loading')}</div>;
  if (!data) return <div className="text-red-400 text-center py-12">{t('listings.loadFailed')}</div>;

  const filtered = filter === 'all' ? data.listings : data.listings.filter(l => l.status === filter);
  const syncedPct = data.total > 0 ? Math.round((data.stats.synced + data.stats.live) / data.total * 100) : 0;

  return (
    <div className="space-y-6" data-testid="listings-tab">
      {/* Error Report Summary */}
      <Card className="bg-white border-gray-200">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-gray-900 text-base">{t('listings.accuracyReport')}</CardTitle>
            <Badge className="bg-blue-900/30 text-blue-400 text-sm font-bold">{syncedPct}%</Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="w-full bg-gray-700 rounded-full h-4 mb-4">
            <div className="h-4 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 transition-all duration-500" style={{width: `${syncedPct}%`}} />
          </div>
          <div className="flex flex-wrap gap-3">
            {Object.entries(data.stats).map(([status, count]) => {
              const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.not_synced;
              return (
                <button
                  key={status}
                  onClick={() => setFilter(filter === status ? 'all' : status)}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs transition-all ${
                    filter === status ? 'ring-1 ring-white/30' : ''
                  } ${cfg.badge}`}
                >
                  <div className={`w-2 h-2 rounded-full ${cfg.color}`} />
                  {t(`listings.statusLabel.${cfg.key}`)}: {count}
                </button>
              );
            })}
            {filter !== 'all' && (
              <button onClick={() => setFilter('all')} className="text-xs text-gray-400 hover:text-gray-900 px-2">
                {t('listings.clearFilter')}
              </button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Directory Listing Table */}
      <Card className="bg-white border-gray-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-gray-900 text-base">
            {t('listings.directoryListings', { count: filtered.length })}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left text-xs text-gray-500 font-medium px-4 py-3">{t('listings.publisher')}</th>
                  <th className="text-left text-xs text-gray-500 font-medium px-4 py-3">{t('listings.statusCol')}</th>
                  <th className="text-left text-xs text-gray-500 font-medium px-4 py-3 hidden sm:table-cell">{t('listings.priorityCol')}</th>
                  <th className="text-left text-xs text-gray-500 font-medium px-4 py-3 hidden md:table-cell">{t('listings.entityName')}</th>
                  <th className="text-right text-xs text-gray-500 font-medium px-4 py-3">{t('listings.linkCol')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((listing, i) => {
                  const cfg = STATUS_CONFIG[listing.status] || STATUS_CONFIG.not_synced;
                  const Icon = cfg.icon;
                  return (
                    <tr key={i} className="border-b border-gray-200/50 hover:bg-white/[0.02] transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-lg bg-gray-800 flex items-center justify-center text-[10px] font-bold text-gray-400 uppercase">
                            {listing.publisher?.charAt(0)}
                          </div>
                          <span className="text-sm text-gray-900 font-medium">{listing.publisher}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <Icon className={`h-3.5 w-3.5 ${cfg.badge.includes('green') ? 'text-green-400' : cfg.badge.includes('blue') ? 'text-blue-400' : cfg.badge.includes('yellow') ? 'text-yellow-400' : cfg.badge.includes('red') ? 'text-red-400' : 'text-gray-400'}`} />
                          <span className="text-xs text-gray-300">{t(`listings.statusLabel.${cfg.key}`)}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell">
                        <Badge className={`text-[10px] ${listing.tier === 'critical' ? 'bg-red-900/20 text-red-400' : listing.tier === 'high' ? 'bg-amber-50 text-amber-400' : listing.tier === 'medium' ? 'bg-blue-50 text-blue-400' : 'bg-gray-800 text-gray-400'}`}>
                          {listing.tier}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <span className="text-sm text-gray-400">{listing.entity_name}</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {listing.listing_url ? (
                          <a href={listing.listing_url} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300">
                            <ExternalLink className="h-3.5 w-3.5" />
                          </a>
                        ) : (
                          <span className="text-gray-600 text-xs">--</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
